import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { t } from 'app/core/i18n/i18n';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import {
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  skip,
  switchMap,
} from 'rxjs';
import {
  BPGroupedApiItem,
  BPGroupedItem,
  BPGroupedResponse,
  BPGroupingCriteria,
} from '../../types/bp-grouping.types';
import { AssignedPartnersPanelService } from '../assigned-partners-panel.service';

export interface GroupedPagination {
  currentPage: number;
  limitPerPage: number;
}

@Injectable()
export class AssignedPartnersGroupedService {
  private businessPartnerService = inject(BusinessPartnerService);
  private panelService = inject(AssignedPartnersPanelService);
  private handlerMessage = inject(HandlerMessageService);

  // === STATE SIGNALS ===
  private _data = signal<BPGroupedItem[]>([]);
  private _totalItems = signal<number>(0);
  private _loading = signal<boolean>(false);
  private _pagination = signal<GroupedPagination>({
    currentPage: 1,
    limitPerPage: 50,
  });

  // === SHARED FROM PANEL ===
  readonly searchTerm = this.panelService.searchTerm;
  readonly groupingCriteria = this.panelService.groupingCriteria;
  readonly subRouteId = this.panelService.subRouteIdForGroupView;

  // === ACTION SUBJECTS ===
  readonly updatePagination$ = new Subject<Partial<GroupedPagination>>();
  readonly reload$ = new Subject<void>();

  // === PUBLIC COMPUTED ===
  readonly data = computed(() => this._data());
  readonly totalItems = computed(() => this._totalItems());
  readonly loading = computed(() => this._loading());
  readonly pagination = computed(() => this._pagination());

  // === OBSERVABLES FROM SIGNALS ===
  private subRouteId$ = toObservable(this.subRouteId);
  private groupingCriteria$ = toObservable(this.groupingCriteria);
  private searchTerm$ = toObservable(this.searchTerm);
  private pagination$ = toObservable(this._pagination);

  constructor() {
    // Pagination updates
    this.updatePagination$.pipe(takeUntilDestroyed()).subscribe((pagination) => {
      this._pagination.update((prev) => ({ ...prev, ...pagination }));
    });

    // Search term resets to page 1
    this.searchTerm$
      .pipe(debounceTime(400), distinctUntilChanged(), skip(1), takeUntilDestroyed())
      .subscribe(() => {
        this._pagination.update((prev) => ({ ...prev, currentPage: 1 }));
      });

    // Grouping criteria change resets to page 1
    this.groupingCriteria$.pipe(skip(1), takeUntilDestroyed()).subscribe(() => {
      this._pagination.update((prev) => ({ ...prev, currentPage: 1 }));
    });

    // Reactive data loading
    // Triggers: subRouteId + groupingCriteria (both required), pagination, reload
    combineLatest([
      this.subRouteId$.pipe(filter((id): id is string => !!id)),
      this.groupingCriteria$.pipe(filter((c) => c !== BPGroupingCriteria.NONE)),
      this.pagination$,
    ])
      .pipe(
        debounceTime(50),
        switchMap(([subRouteId, groupBy, pagination]) => {
          this._loading.set(true);
          return this.businessPartnerService.countAssignedByBpFrequency(
            subRouteId,
            groupBy,
            {
              currentPage: pagination.currentPage,
              limitPerPage: pagination.limitPerPage,
              search: this.searchTerm(),
              sort: 'name',
              sortDirection: 'ASC',
            },
            this.panelService.subRouteData()?.company?._id?.toString() ?? null
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (response) => {
          const groupedData: BPGroupedResponse | undefined = response?.data;
          const dataArray: BPGroupedApiItem[] = groupedData?.data ?? [];

          if (dataArray.length > 0) {
            // Map API response to BPGroupedItem format
            const items: BPGroupedItem[] = dataArray.map((item, index) => {
              // _id para tracking en template, id numérico original para filtros
              const trackingId =
                item._id || (item.id != null ? String(item.id) : `no-group-${item.name || index}`);
              return {
                _id: trackingId,
                id: item.id ?? null, // Mantener ID numérico original para filtros
                name: item.name || t('routing.SUB_ROUTE_PARTNERS.GROUPED.NO_NAME_FALLBACK'),
                // i18n-exempt: 'S/N' is also a comparison sentinel (see buildFiltersForGroup) — not display copy
                code: item.code || 'S/N',
                totalBusinessPartners: item.totalBusinessPartners || item.count || 0,
                businessPartners: item.businessPartners || [],
              };
            });
            this._data.set(items);
            this._totalItems.set(groupedData?.pageInfo?.[0]?.total ?? items.length);
          } else {
            this._data.set([]);
            this._totalItems.set(0);
          }
          this._loading.set(false);
        },
        error: (err) => {
          this._loading.set(false);
          this._data.set([]);
          this.handlerMessage.error({
            message: err?.error?.message,
            alternativeMessage: t('routing.SUB_ROUTE_PARTNERS.ERRORS.LOAD_FAILED'),
          });
        },
      });

    // Manual reload
    this.reload$.pipe(takeUntilDestroyed()).subscribe(() => {
      // Trigger reload by updating pagination with same values
      this._pagination.update((p) => ({ ...p }));
    });
  }
}
